/**
 * Montagem e envio de batches (delivery).
 */

import type { BatchEventRequest } from '@/types';
import { buildEvent, type SendEventParams } from '../../utils/sendEvent';
import { sendBatch } from '../../utils/sendBatch';
import { recordBatchFailure, recordBatchSuccess } from '../circuitBreaker';
import { maybeCompressPayload } from '../compression';
import { signEventPayload } from '../eventSignature';
import {
  enrichEventDataWithGroup,
  extractTransactionGroup,
} from '../ordering/transactionGroup';
import type { QueueItem } from './types';

export function buildBatchRequest(
  items: QueueItem[],
  fingerprint: string
): BatchEventRequest {
  const first = items[0];
  const { config, visitorId, sessionId } = first.params;
  const version = first.params.version ?? '';
  const scriptUrl = first.params.scriptUrl;
  return {
    pixel_code: config.pixel,
    visitor_id: visitorId,
    session_id: sessionId,
    batch_id: crypto.randomUUID(),
    sent_at: Date.now(),
    meta: {
      version,
      pixel_source: 'gomarke',
      environment: 'production',
      script_url: scriptUrl ?? '',
      projectId: config.projectId ?? '',
      pageId: config.pageId ?? '',
    },
    events: items.map(item => {
      const group = extractTransactionGroup(item.params.eventData);
      const enriched = enrichEventDataWithGroup(item.params.eventData, group);
      const withSeq = {
        ...(enriched as Record<string, unknown>),
        session_sequence: item.sessionSequence,
      };
      return buildEvent(
        { ...item.params, eventData: withSeq as SendEventParams['eventData'] },
        fingerprint,
        item.id,
        item.occurredAt ?? item.createdAt
      );
    }),
  };
}

export async function deliverBatch(
  batchItems: QueueItem[],
  fingerprint: string
): Promise<Awaited<ReturnType<typeof sendBatch>>> {
  const config = batchItems[0].params.config;
  let batch = buildBatchRequest(batchItems, fingerprint);

  if (config.apiKey) {
    const sig = await signEventPayload(JSON.stringify(batch.events), config.apiKey);
    if (sig) {
      batch = {
        ...batch,
        meta: { ...batch.meta, signature: sig } as BatchEventRequest['meta'],
      };
    }
  }

  maybeCompressPayload(JSON.stringify(batch), false);

  if (config.debug) {
    const bytes = new TextEncoder().encode(JSON.stringify(batch)).length;
    console.log(
      `[gomarke] sending batch: count=${batch.events.length}, bytes=${bytes}`
    );
  }

  try {
    const response = await sendBatch(batch, config);
    recordBatchSuccess();
    return response;
  } catch (error) {
    recordBatchFailure();
    throw error;
  }
}
